import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Target, TrendingUp, Users, BarChart3, Globe, Search, MapPin, Eye, Zap, CheckCircle, Phone, Mail, Camera, Star, DollarSign, Clock, Layers, Building } from "lucide-react";

export default function BriefAds() {
  const [tab, setTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview Progetto" },
    { id: "google", label: "Google Ads" },
    { id: "linkedin", label: "LinkedIn Ads" },
    { id: "assets", label: "Asset & Creatività" },
    { id: "tracking", label: "Tracking & KPI" },
    { id: "budget", label: "Budget" },
  ];

  return (
    <div className="min-h-screen bg-[#0c1220] text-white" style={{ fontFamily: "Poppins, sans-serif" }}>
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[#D4AF37] font-black text-2xl">schedabusiness</span>
            <span className="px-2 py-0.5 rounded bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold">BRIEF ADS</span>
          </div>
          <p className="text-[#9CA3AF] text-sm">Documento operativo per Giorgio (Google Ads) e Jessica Ventura (LinkedIn) — Progetto TANDEM (.it + .com)</p>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="border-b border-white/10 px-6 sticky top-0 bg-[#0c1220]/95 backdrop-blur-xl z-50">
        <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto py-2">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? "bg-[#D4AF37] text-black" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* OVERVIEW */}
        {tab === "overview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <section>
              <h2 className="text-3xl font-black mb-6">Il Progetto</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                  <h3 className="text-lg font-bold text-[#D4AF37] mb-3">Chi siamo</h3>
                  <p className="text-[#9CA3AF] leading-relaxed">
                    <strong className="text-white">schedabusiness.com</strong> — Servizi B2B per profili Google Business: Virtual Tour 360°, ottimizzazione schede, formazione, recupero sospensioni. Zona operativa: <strong className="text-white">Siena e Valdelsa Senese</strong> (Colle Val d'Elsa, Poggibonsi, San Gimignano, Casole d'Elsa, Monteriggioni).
                  </p>
                  <p className="text-[#9CA3AF] mt-3">Fotografo Google Street View Certificato dal 2016. <strong className="text-white">7.000+ foto pubblicate</strong>, <strong className="text-white">52 milioni di views</strong> su Google Maps.</p>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                  <h3 className="text-lg font-bold text-[#D4AF37] mb-3">Il TANDEM</h3>
                  <p className="text-[#9CA3AF] leading-relaxed">Due domini, un brand:</p>
                  <ul className="mt-2 space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#34A853] mt-0.5 flex-shrink-0" />
                      <span><strong className="text-white">schedabusiness.it</strong> — Portale vendita B2B (pacchetti, pricing, WhatsApp conversion)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#4285F4] mt-0.5 flex-shrink-0" />
                      <span><strong className="text-white">schedabusiness.com</strong> — Vetrina portfolio, credibilità, SEO organico, assistenza</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span><strong className="text-white">Widget chatbot</strong> condiviso cross-domain (AI Claude)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Target Clientela</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Building, label: "Agenzie Immobiliari", desc: "Virtual Tour per vendita immobili, open house virtuali", color: "#4285F4" },
                  { icon: Users, label: "Enti Pubblici & Scuole", desc: "Musei, comuni, scuole — digitalizzazione spazi", color: "#34A853" },
                  { icon: MapPin, label: "Attività Locali", desc: "Ristoranti, hotel, B&B, negozi — visibilità Google Maps", color: "#EA4335" },
                ].map((t, i) => (
                  <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10">
                    <t.icon className="w-6 h-6 mb-2" style={{ color: t.color }} />
                    <h4 className="font-bold text-white text-sm mb-1">{t.label}</h4>
                    <p className="text-[#9CA3AF] text-xs">{t.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 rounded-xl bg-[#EA4335]/10 border border-[#EA4335]/20">
                <p className="text-sm text-[#9CA3AF]"><strong className="text-white">Nota importante:</strong> Il target include persone 65+ anni. Il sito ha UI "Boomer-friendly" con font grandi. Le ads devono essere chiare, dirette, senza gergo tecnico.</p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Pacchetti in vendita</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-gradient-to-br from-[#EA4335] to-[#c62828]">
                  <h4 className="font-black text-white text-lg">START €99</h4>
                  <p className="text-white/70 text-xs mt-1">Verifica, claim, correzione scheda Google. Il primo passo.</p>
                  <p className="text-white/50 text-xs mt-2">PayPal 3 rate €33 TAEG 0%</p>
                </div>
                <div className="p-5 rounded-xl bg-gradient-to-br from-[#FBBC05] to-[#e5a100]">
                  <h4 className="font-black text-black text-lg">PLUS €299</h4>
                  <p className="text-black/60 text-xs mt-1">Recupero sospensione, recensioni, 5 foto pro, consulenza 1h.</p>
                  <p className="text-black/40 text-xs mt-2">IL PIÙ VENDUTO · 3 rate €99,66</p>
                </div>
                <div className="p-5 rounded-xl bg-gradient-to-br from-[#34A853] to-[#1b7a33]">
                  <h4 className="font-black text-white text-lg">ROOFTOP €599</h4>
                  <p className="text-white/70 text-xs mt-1">Tutto incluso + Virtual Tour 360° + Formazione IN REGALO.</p>
                  <p className="text-white/50 text-xs mt-2">TOP DI GAMMA · 3 rate €199,67</p>
                </div>
              </div>
              <p className="text-sm text-[#9CA3AF] mt-3"><strong className="text-white">Formazione 100% deducibile</strong> per P.IVA e imprese. Paghi ora, detrai nel 2027.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Link Utili</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { label: "Sito .it (vendita)", url: "https://boomer-friendly-tour.preview.emergentagent.com", color: "#D4AF37" },
                  { label: "Sito .com (portfolio)", url: "https://tour-optimizer-4.preview.emergentagent.com", color: "#4285F4" },
                  { label: "Profilo Google Maps", url: "https://g.page/r/CW-WqgxqiG1NEBM", color: "#34A853" },
                  { label: "WhatsApp Business", url: "https://wa.me/+393273839782", color: "#25D366" },
                ].map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group">
                    <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: link.color }} />
                    <span className="text-sm font-medium text-white">{link.label}</span>
                    <ArrowRight className="w-3 h-3 text-[#9CA3AF] ml-auto group-hover:translate-x-1 transition-transform" />
                  </a>
                ))}
              </div>
            </section>
          </motion.div>
        )}

        {/* GOOGLE ADS */}
        {tab === "google" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <h2 className="text-3xl font-black">Google Ads — Strategia</h2>
            
            <section className="p-6 rounded-2xl bg-[#4285F4]/10 border border-[#4285F4]/20">
              <h3 className="text-lg font-bold text-[#4285F4] mb-3 flex items-center gap-2"><Target className="w-5 h-5" /> Obiettivo</h3>
              <p className="text-white text-lg font-medium">Lead generation via WhatsApp + telefonate dirette</p>
              <p className="text-[#9CA3AF] mt-1">Conversione: click su WhatsApp, chiamata telefonica, compilazione form (futuro)</p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Campagne Suggerite</h3>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="w-5 h-5 text-[#4285F4]" />
                    <h4 className="font-bold text-white">1. Search — Emergenza Scheda Google</h4>
                    <span className="px-2 py-0.5 rounded bg-[#EA4335]/20 text-[#EA4335] text-xs font-bold ml-auto">PRIORITÀ ALTA</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm mb-3">Intercetta chi ha problemi URGENTI con la scheda Google (sospensione, recensioni false, profilo bloccato).</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-white mb-1">Keyword suggerite:</p>
                      <ul className="text-xs text-[#9CA3AF] space-y-1">
                        <li>• "scheda google sospesa"</li>
                        <li>• "recupero profilo google business"</li>
                        <li>• "recensioni false google come rimuovere"</li>
                        <li>• "ottimizzazione scheda google maps"</li>
                        <li>• "fotografo google street view [città]"</li>
                        <li>• "virtual tour 360 [città]"</li>
                        <li>• "scheda google bloccata aiuto"</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white mb-1">Geo-targeting:</p>
                      <ul className="text-xs text-[#9CA3AF] space-y-1">
                        <li>• Siena (provincia)</li>
                        <li>• Colle di Val d'Elsa</li>
                        <li>• Poggibonsi</li>
                        <li>• San Gimignano</li>
                        <li>• Casole d'Elsa</li>
                        <li>• Monteriggioni</li>
                        <li>• Raggio: 30-50km da Colle Val d'Elsa</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-3 p-3 rounded-lg bg-white/5">
                    <p className="text-xs font-bold text-white mb-1">Copy annuncio suggerito:</p>
                    <p className="text-sm text-[#D4AF37] font-bold">Scheda Google Sospesa? Risolvo io in 24h</p>
                    <p className="text-xs text-[#9CA3AF]">Recupero profilo, rimozione recensioni false, Virtual Tour 360°. Fotografo Google Certificato dal 2016. START €99 — Soddisfatti o Rimborsati.</p>
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-5 h-5 text-[#34A853]" />
                    <h4 className="font-bold text-white">2. Local Services / Maps Ads</h4>
                    <span className="px-2 py-0.5 rounded bg-[#FBBC05]/20 text-[#FBBC05] text-xs font-bold ml-auto">PRIORITÀ MEDIA</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm mb-3">Comparire sopra i risultati organici di Google Maps quando cercano servizi fotografici/virtuali nella zona.</p>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs font-bold text-white mb-1">Note per Giorgio:</p>
                    <p className="text-xs text-[#9CA3AF]">Il profilo Google è già ottimizzato con 5.0 stelle (51 recensioni). Valutare se attivare Local Services Ads per "fotografo" e "virtual tour" nella zona Siena.</p>
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-5 h-5 text-[#FBBC05]" />
                    <h4 className="font-bold text-white">3. Display/YouTube — Remarketing</h4>
                    <span className="px-2 py-0.5 rounded bg-[#34A853]/20 text-[#34A853] text-xs font-bold ml-auto">FASE 2</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm">Retargeting su chi ha visitato il sito .it o .com senza convertire. Banner con "Soddisfatti o Rimborsati" + prezzo START €99.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Landing Page per Ads</h3>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-[#9CA3AF] text-sm mb-2">Le ads dovrebbero puntare a:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-[#EA4335] mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Emergenza/Sospensione:</strong> schedabusiness.it → sezione START €99 (anchor #start99)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-[#FBBC05] mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Pacchetti/Pricing:</strong> schedabusiness.it → sezione Pacchetti (anchor #pacchetti)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-[#34A853] mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Virtual Tour:</strong> schedabusiness.it → sezione Demo 360° (anchor #demo)</span>
                  </li>
                </ul>
                <p className="text-xs text-[#9CA3AF] mt-3">Nota: possiamo creare landing page dedicate per ogni campagna se richiesto da Giorgio/Jessica.</p>
              </div>
            </section>
          </motion.div>
        )}

        {/* LINKEDIN */}
        {tab === "linkedin" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <h2 className="text-3xl font-black">LinkedIn Ads — Strategia</h2>
            
            <section className="p-6 rounded-2xl bg-[#0A66C2]/10 border border-[#0A66C2]/20">
              <h3 className="text-lg font-bold text-[#0A66C2] mb-3 flex items-center gap-2"><Users className="w-5 h-5" /> Obiettivo</h3>
              <p className="text-white text-lg font-medium">Brand awareness B2B + lead qualificati (decision maker)</p>
              <p className="text-[#9CA3AF] mt-1">Target: titolari agenzie immobiliari, responsabili enti pubblici, dirigenti scolastici nella zona Siena</p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Campagne Suggerite</h3>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-[#0A66C2]" />
                    <h4 className="font-bold text-white">1. Sponsored Content — Formazione Deducibile</h4>
                    <span className="px-2 py-0.5 rounded bg-[#0A66C2]/20 text-[#0A66C2] text-xs font-bold ml-auto">PRIORITÀ ALTA</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm mb-3">Post sponsorizzato nel feed LinkedIn che promuove la formazione 100% deducibile per P.IVA.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-white mb-1">Targeting:</p>
                      <ul className="text-xs text-[#9CA3AF] space-y-1">
                        <li>• Titolo: Titolare, CEO, Direttore, Responsabile Marketing</li>
                        <li>• Settore: Immobiliare, Pubblica Amministrazione, Istruzione</li>
                        <li>• Località: Toscana (focus Siena)</li>
                        <li>• Dimensione azienda: 1-200 dipendenti</li>
                        <li>• Funzione: Business Development, Marketing, Operations</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white mb-1">Copy suggerito:</p>
                      <div className="p-2 rounded-lg bg-white/5 text-xs text-[#9CA3AF]">
                        <p className="text-[#D4AF37] font-bold text-sm mb-1">La tua scheda Google è il tuo biglietto da visita digitale.</p>
                        <p>Il 97% dei consumatori cerca online prima di visitare un'attività locale. Quanto ti costa NON essere visibile?</p>
                        <p className="mt-1">Formazione 100% deducibile · Virtual Tour 360° · Fotografo Google Certificato dal 2016</p>
                        <p className="text-white font-bold mt-1">START da €99 → Soddisfatti o Rimborsati</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-[#0A66C2]" />
                    <h4 className="font-bold text-white">2. InMail — Outreach diretto enti/scuole</h4>
                    <span className="px-2 py-0.5 rounded bg-[#FBBC05]/20 text-[#FBBC05] text-xs font-bold ml-auto">FASE 2</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm">Messaggi diretti a dirigenti scolastici e responsabili comunali nella zona. Proposta: digitalizzazione spazi con Virtual Tour per trasparenza e accessibilità.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Note per Jessica</h3>
              <div className="p-5 rounded-xl bg-[#0A66C2]/10 border border-[#0A66C2]/20">
                <ul className="space-y-2 text-sm text-[#9CA3AF]">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#0A66C2] mt-0.5 flex-shrink-0" /><span>Il .com ha il portfolio completo per dare credibilità alle ads</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#0A66C2] mt-0.5 flex-shrink-0" /><span>Il .it ha i pacchetti con pricing chiaro per la conversione</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#0A66C2] mt-0.5 flex-shrink-0" /><span>La formazione deducibile è il cavallo di battaglia per il target B2B su LinkedIn</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#0A66C2] mt-0.5 flex-shrink-0" /><span>Riccardo ha 7.000+ foto e 52M views — dati REALI da usare come social proof</span></li>
                </ul>
              </div>
            </section>
          </motion.div>
        )}

        {/* ASSETS */}
        {tab === "assets" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <h2 className="text-3xl font-black">Asset & Creatività</h2>
            
            <section>
              <h3 className="text-xl font-bold mb-4">Brand Identity</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="font-bold text-sm mb-3">Colori Brand</h4>
                  <div className="space-y-2">
                    {[
                      { name: "Oro TANDEM", hex: "#D4AF37", note: "Colore primario unificato .it + .com" },
                      { name: "Nero Dark", hex: "#0c1220", note: "Background" },
                      { name: "Google Blue", hex: "#4285F4", note: "" },
                      { name: "Google Red", hex: "#EA4335", note: "" },
                      { name: "Google Yellow", hex: "#FBBC05", note: "" },
                      { name: "Google Green", hex: "#34A853", note: "" },
                    ].map((c, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex-shrink-0 border border-white/10" style={{ backgroundColor: c.hex }} />
                        <div>
                          <span className="text-white text-sm font-medium">{c.name}</span>
                          <span className="text-[#9CA3AF] text-xs ml-2">{c.hex}</span>
                          {c.note && <span className="text-[#9CA3AF] text-xs ml-2">— {c.note}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="font-bold text-sm mb-3">Font & Tono</h4>
                  <ul className="space-y-2 text-sm text-[#9CA3AF]">
                    <li><strong className="text-white">Font:</strong> Poppins 900 (Black) — per titoli e CTA</li>
                    <li><strong className="text-white">Tono:</strong> Diretto, autorevole, PNL/NLP oriented</li>
                    <li><strong className="text-white">USP:</strong> "se non ti trovano, non esisti"</li>
                    <li><strong className="text-white">Garanzia:</strong> "Soddisfatti o Rimborsati"</li>
                    <li><strong className="text-white">Certificazione:</strong> "Fotografo Google Street View Certificato dal 2016"</li>
                    <li><strong className="text-white">Social Proof:</strong> "7.000+ foto · 52M views · 5.0 stelle (51 recensioni)"</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Headline testate (da usare nelle ads)</h3>
              <div className="space-y-2">
                {[
                  "Scheda Google Sospesa? Risolvo io — START €99",
                  "La Tua Scheda Google Merita di Più",
                  "Se non ti trovano, non esisti.",
                  "Virtual Tour 360° — Il tuo cliente entra prima di entrare",
                  "Formazione Google 100% Deducibile per P.IVA",
                  "7.000+ foto su Google Maps. La prossima è la tua.",
                  "Soddisfatti o Rimborsati — Presa in carico entro 24h",
                  "Il 97% cerca online. Tu come ti fai trovare?",
                ].map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <span className="text-[#D4AF37] font-bold text-xs w-6">{i + 1}.</span>
                    <span className="text-white text-sm font-medium">{h}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Immagini/Video disponibili</h3>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <ul className="space-y-2 text-sm text-[#9CA3AF]">
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Virtual Tour demo interattivo sul sito .it</li>
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Animated Short 20s (storytelling Google Maps) — usabile come video ad</li>
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Screenshot analytics crescita (2x views, +42% indicazioni, +35% clic)</li>
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Badge Google Street View Trusted (SVG + PNG)</li>
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Logo ufficiale schedabusiness.com (oro su nero)</li>
                  <li><CheckCircle className="w-4 h-4 text-[#34A853] inline mr-2" />Foto 360° e panoramiche della zona (Colle Val d'Elsa, San Gimignano)</li>
                </ul>
              </div>
            </section>
          </motion.div>
        )}

        {/* TRACKING */}
        {tab === "tracking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <h2 className="text-3xl font-black">Tracking & KPI</h2>
            
            <section className="p-6 rounded-2xl bg-[#EA4335]/10 border border-[#EA4335]/20">
              <h3 className="text-lg font-bold text-[#EA4335] mb-3 flex items-center gap-2"><Zap className="w-5 h-5" /> Azione richiesta a Giorgio/Jessica</h3>
              <p className="text-white font-medium">Per implementare il tracking su entrambi i domini (.it + .com), servono:</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#4285F4] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">1</span>
                  <span className="text-[#9CA3AF]"><strong className="text-white">Google Ads Conversion ID</strong> — formato AW-XXXXXXXXX</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#4285F4] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">2</span>
                  <span className="text-[#9CA3AF]"><strong className="text-white">Google Ads Conversion Label</strong> — per ogni evento (WhatsApp click, chiamata, scroll pacchetti)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#0A66C2] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">3</span>
                  <span className="text-[#9CA3AF]"><strong className="text-white">LinkedIn Partner ID</strong> — per il pixel LinkedIn Insight Tag</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#34A853] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">4</span>
                  <span className="text-[#9CA3AF]"><strong className="text-white">Google Analytics 4 Measurement ID</strong> — formato G-XXXXXXXXXX (opzionale se già attivo)</span>
                </li>
              </ul>
              <p className="text-xs text-[#9CA3AF] mt-4">Una volta ricevuti gli ID, li installiamo su entrambi i domini in parallelo in meno di 1 ora.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Eventi di conversione da tracciare</h3>
              <div className="space-y-3">
                {[
                  { event: "whatsapp_click", desc: "Click su qualsiasi bottone WhatsApp", priority: "CRITICO", color: "#EA4335" },
                  { event: "phone_call", desc: "Click su numero telefono", priority: "CRITICO", color: "#EA4335" },
                  { event: "package_click", desc: "Click su card pacchetto (START/PLUS/ROOFTOP)", priority: "ALTO", color: "#FBBC05" },
                  { event: "demo_360_view", desc: "Interazione con il Virtual Tour 360°", priority: "MEDIO", color: "#34A853" },
                  { event: "scroll_75", desc: "Scroll al 75% della pagina", priority: "MEDIO", color: "#34A853" },
                  { event: "chatbot_open", desc: "Apertura chatbot AI", priority: "BASSO", color: "#4285F4" },
                ].map((e, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-white/5 border border-white/10">
                    <code className="text-[#D4AF37] text-xs bg-white/5 px-2 py-1 rounded">{e.event}</code>
                    <span className="text-white text-sm flex-1">{e.desc}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${e.color}20`, color: e.color }}>{e.priority}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">KPI da monitorare</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="font-bold text-[#4285F4] text-sm mb-2">Google Ads</h4>
                  <ul className="space-y-1 text-xs text-[#9CA3AF]">
                    <li>• CPC (Costo per Click)</li>
                    <li>• CTR (Click-Through Rate)</li>
                    <li>• Conversioni WhatsApp/Telefono</li>
                    <li>• Costo per Lead</li>
                    <li>• Quality Score keyword</li>
                    <li>• Impression share nella zona</li>
                  </ul>
                </div>
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="font-bold text-[#0A66C2] text-sm mb-2">LinkedIn Ads</h4>
                  <ul className="space-y-1 text-xs text-[#9CA3AF]">
                    <li>• CPM (Costo per 1000 impressioni)</li>
                    <li>• Engagement Rate sui post</li>
                    <li>• Click to Website</li>
                    <li>• Lead Form Completions</li>
                    <li>• Demografia audience raggiunta</li>
                    <li>• Frequenza per utente</li>
                  </ul>
                </div>
              </div>
            </section>
          </motion.div>
        )}

        {/* BUDGET */}
        {tab === "budget" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <h2 className="text-3xl font-black">Budget</h2>
            
            <section className="p-6 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20">
              <h3 className="text-2xl font-black text-[#D4AF37] mb-2">€800 totali</h3>
              <p className="text-[#9CA3AF]">€400 investimento schedabusiness + €400 budget media (pagati a Google/LinkedIn)</p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Proposta allocazione (da validare con Giorgio e Jessica)</h3>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white flex items-center gap-2"><Search className="w-4 h-4 text-[#4285F4]" /> Google Ads</h4>
                    <span className="text-[#D4AF37] font-black text-lg">€300</span>
                  </div>
                  <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-[#4285F4]" style={{ width: "75%" }} />
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-2">75% del budget media — Search intent alto, conversione diretta WhatsApp/telefono</p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white flex items-center gap-2"><Users className="w-4 h-4 text-[#0A66C2]" /> LinkedIn Ads</h4>
                    <span className="text-[#D4AF37] font-black text-lg">€100</span>
                  </div>
                  <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-[#0A66C2]" style={{ width: "25%" }} />
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-2">25% del budget media — Brand awareness B2B, seminare per raccogliere dopo</p>
                </div>
              </div>
              <p className="text-sm text-[#9CA3AF] mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white">Nota:</strong> Questa è una proposta iniziale. Giorgio e Jessica decidono l'allocazione finale in base alla strategia. Se dicono 100% Google Ads, si fa 100% Google Ads. Loro sono i Capitani.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">ROI atteso</h3>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-[#9CA3AF] text-sm mb-3">Con un budget di €400 su Google Ads (Search) nella zona Siena:</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-black text-[#4285F4]">~200-400</p>
                    <p className="text-xs text-[#9CA3AF]">Click stimati</p>
                    <p className="text-xs text-[#9CA3AF]">(CPC €1-2 keyword locali)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-[#34A853]">~10-20</p>
                    <p className="text-xs text-[#9CA3AF]">Lead WhatsApp/Tel</p>
                    <p className="text-xs text-[#9CA3AF]">(conversion rate 5%)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-[#D4AF37]">~3-5</p>
                    <p className="text-xs text-[#9CA3AF]">Clienti chiusi</p>
                    <p className="text-xs text-[#9CA3AF]">(close rate 25-30%)</p>
                  </div>
                </div>
                <p className="text-sm text-[#9CA3AF] mt-4">Con 3-5 clienti a €99-599 di media = <strong className="text-[#D4AF37]">€300-3.000 di fatturato</strong> su €400 investiti. ROI positivo già dal primo mese se i lead convertono.</p>
              </div>
            </section>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 px-6 py-6 text-center">
        <p className="text-[#9CA3AF] text-xs">schedabusiness.com — Brief Ads per Giorgio e Jessica · Progetto TANDEM · Aprile 2026</p>
        <p className="text-[#9CA3AF] text-xs mt-1">Contatto: <a href="mailto:assistenza@schedabusiness.com" className="text-[#D4AF37] hover:underline">assistenza@schedabusiness.com</a> · <a href="https://wa.me/+393273839782" className="text-[#25D366] hover:underline">WhatsApp</a></p>
      </footer>
    </div>
  );
}
